import * as Yup from 'yup'
import { TFunction } from 'i18next'
import { getGovernmentJobTitle } from '../helpers'
import { Currency, GovernmentJobTitle } from '../../../../types'

// Obsolete method as validation is moved to BE - Salary validation is missing
export const getBrazilianContractChangeFormValidationSchema = (
  annualSalary: number,
  salaryCurrency: Currency,
  t: TFunction,
  governmentJobTitles?: GovernmentJobTitle[],
): Yup.ObjectSchema => {
  return Yup.object().shape({
    changes: Yup.object().shape(
      {
        salary: Yup.number()
          .transform((value) => (isNaN(value) ? undefined : value))
          .test({
            name: 'salaryChange',
            // arrow functions cannot be used here because of different behavior in this binding
            test: function () {
              const context: any = this.options.context
              const { salary, bonus, employeeLevel, governmentJobTitle } = context.changes

              // default case at least one field needs to be filled out -> add the dropdown
              if (salary === undefined && employeeLevel === undefined && bonus === undefined && governmentJobTitle === undefined) {
                return this.createError({
                  message: t('formError.conditionalRequired', {
                    fieldNames: `${t('appraisalMeeting.contract.changes.form.salary')},
                ${t('appraisalMeeting.contract.employeeLevel')},
                ${t('appraisalMeeting.contract.bonus')},
                ${t('appraisalMeeting.contract.governmentJobTitle')}`,
                  }),
                  path: 'changes.salary',
                })
              }

              // validFrom is not defined so validation for vaildForm will fail. It's safe to exit.
              if (context.validFrom === undefined) {
                return true
              }
              if (salary !== undefined && salary < annualSalary) {
                return this.createError({
                  message: t('formError.salaryMustBeHigherThanPrevious', {
                    fieldName: t(`appraisalMeeting.contract.changes.form.salary`),
                    minAmount: annualSalary,
                    currency: salaryCurrency,
                  }),
                  path: 'changes.salary',
                })
              }
              return true
            },
          }),
        bonus: Yup.number()
          .transform((value) => (isNaN(value) ? undefined : value))
          .test({
            name: 'TargetBonusTest',
            test: function () {
              const context: any = this.options.context
              const { salary, bonus, employeeLevel, governmentJobTitle } = context.changes

              if (salary === undefined && employeeLevel === undefined && bonus === undefined && governmentJobTitle === undefined) {
                return this.createError({
                  message: t('formError.conditionalRequired', {
                    fieldNames: `${t('appraisalMeeting.contract.changes.form.salary')},
                ${t('appraisalMeeting.contract.employeeLevel')},
                ${t('appraisalMeeting.contract.bonus')},
                ${t('appraisalMeeting.contract.governmentJobTitle')}`,
                  }),
                  path: 'changes.bonus',
                })
              }

              return true
            },
          }),

        employeeLevel: Yup.string()
          .transform((value) => (isNaN(value) ? undefined : value))
          .test({
            name: 'EmployeeLevelTest',
            test: function () {
              const context: any = this.options.context
              const { salary, bonus, employeeLevel, governmentJobTitle } = context.changes

              if (salary === undefined && employeeLevel === undefined && bonus === undefined && governmentJobTitle === undefined) {
                return this.createError({
                  message: t('formError.conditionalRequired', {
                    fieldNames: `${t('appraisalMeeting.contract.changes.form.salary')},
                ${t('appraisalMeeting.contract.employeeLevel')},
                ${t('appraisalMeeting.contract.bonus')},
                ${t('appraisalMeeting.contract.governmentJobTitle')}`,
                  }),
                  path: 'changes.employeeLevel',
                })
              }

              // validFrom is not defined so validation for validForm will fail. It's safe to exit.
              if (context.validFrom === undefined) {
                return true
              }

              if (employeeLevel !== undefined) {
                if (!governmentJobTitle) {
                  return this.createError({
                    message: t('formError.governmentJobTitleRequired'),
                    path: 'changes.employeeLevel',
                  })
                }
                const selectedGjt = getGovernmentJobTitle(governmentJobTitles || [], governmentJobTitle)
                if (!!selectedGjt && selectedGjt.title && selectedGjt.level !== employeeLevel) {
                  return this.createError({
                    message: t('formError.invalidEmployeeLevel', {
                      path: 'changes.employeeLevel',
                    }),
                  })
                }
              }

              return true
            },
          }),

        governmentJobTitle: Yup.string()
          .transform((value) => (isNaN(value) ? undefined : value))
          .test({
            name: 'GovernmentJobTitleTest',
            test: function () {
              const context: any = this.options.context
              const { salary, bonus, employeeLevel, governmentJobTitle } = context.changes

              if (salary === undefined && employeeLevel === undefined && bonus === undefined && governmentJobTitle === undefined) {
                return this.createError({
                  message: t('formError.conditionalRequired', {
                    fieldNames: `${t('appraisalMeeting.contract.changes.form.salary')},
                ${t('appraisalMeeting.contract.employeeLevel')},
                ${t('appraisalMeeting.contract.bonus')},
                ${t('appraisalMeeting.contract.nationalReferenceLevel')}`,
                  }),
                  path: 'changes.governmentJobTitle',
                })
              }

              // validFrom is not defined so validation for vaildForm will fail. It's safe to exit.
              if (context.validFrom === undefined) {
                return true
              }

              if (governmentJobTitle !== undefined) {
                if (employeeLevel === undefined) {
                  return this.createError({
                    message: t('formError.employeeLevelRequired'),
                    path: 'changes.governmentJobTitle',
                  })
                }
                const selectedGjt = getGovernmentJobTitle(governmentJobTitles || [], governmentJobTitle)
                if (!selectedGjt) {
                  return this.createError({
                    message: t('formError.governmentJobTitleNotFound', {
                      path: 'changes.governmentJobTitle',
                    }),
                  })
                }
                if (selectedGjt.level !== employeeLevel) {
                  return this.createError({
                    message: t('formError.invalidEmployeeLevel', {
                      path: 'changes.governmentJobTitle',
                    }),
                  })
                }
              }
              return true
            },
          }),
      },
      // the following list of fields is used in the noSoftEdges parameter that is barely documented
      // https://gitmemory.com/issue/jquense/yup/720/564591045
      // it is basically a hack to force a certain sorting of fields to avoid cyclical dependencies
      // it is unclear if it is needed here, it will remain and needs to be analyzed in a different ticket
      [
        ['bonus', 'salary'],
        ['employeeLevel', 'salary'],
        ['employeeLevel', 'bonus'],
      ],
    ),

    validFrom: Yup.string().required(t('formError.required', { fieldName: t(`appraisalMeeting.contract.changes.form.validFrom`) })),
  })
}
